import { IconButton, Spinner, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  sessionCheckpointsState,
  type SessionCheckpointView,
  type TrainingEvalRunSummaryView,
} from "@/api";
import { useShareLinkIssuer } from "../shareLinks/useShareLinkIssuer";
import type { LoadState } from "@/components/dataset";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import type { CheckpointTag } from "@/modules/training/public";
import { successRateAbsence, successRateEmptyLabel } from "@/modules/evaluation/public";
import type { TrainingSessionDetailController } from "../useTrainingSessionDetailController";
import { buildCheckpointRows, isSameCheckpointTag } from "./analysis/sessionAnalysisModel";
import { createCheckpointTableRows, type CheckpointTableRow } from "./CheckpointTableRow";
import { CompanionFilesSection } from "./CompanionFilesSection";
import { alwaysFetchedMetricKey, type SessionMetricsController } from "./useSessionMetrics";
import styles from "./TrainingSessionDetailPanels.module.css";
import { RawCheckpointsSection, type CheckpointShareAction } from "./RawCheckpointsSection";
import { useSessionCheckpoints } from "./useSessionCheckpoints";

type CheckpointsPanelProps = {
  controller: TrainingSessionDetailController;
  metrics: SessionMetricsController;
  summariesState: LoadState<TrainingEvalRunSummaryView[]>;
  // 여는 쪽(Policy 이름 링크)이 가리킨 체크포인트. 그 행을 강조하고 처음 그려질 때 한 번 스크롤한다.
  // 표에 없는 태그면(정리됨·파싱 불가) 아무것도 표시하지 않는다.
  focusedCheckpoint?: CheckpointTag | null;
};

// Exported·Raw·부속 파일의 빈 목록·오류가 서로 다른 section 을 가리지 않게 한다.
export function TrainingSessionCheckpointsPanel({
  active,
  ...props
}: CheckpointsPanelProps & { active: boolean }): ReactNode {
  const { controller } = props;
  const { rawState, companionState, exportedRefreshing, refresh } = useSessionCheckpoints(
    controller.session.id,
    active,
    controller.refreshCheckpoints,
  );
  const refreshing =
    exportedRefreshing || rawState.status === "loading" || companionState.status === "loading";
  // 두 표가 발급 흐름 하나를 함께 쓴다. 한 번에 하나만 발급하고, 클립보드 폴백 모달도 하나다.
  const shareIssuer = useShareLinkIssuer();
  const { issue: issueShareLink } = shareIssuer;
  const { shareCheckpoint } = controller;
  const share = useMemo<CheckpointShareAction>(
    () => ({
      pendingKey: shareIssuer.pendingKey,
      share: (key, target) => void issueShareLink(key, () => shareCheckpoint(target)),
    }),
    [shareCheckpoint, shareIssuer.pendingKey, issueShareLink],
  );
  return (
    <div className={styles.checkpoints}>
      <div className={styles.toolbar}>
        <IconButton
          text="새로고침"
          size="sm"
          variant="surface"
          disabled={!active || refreshing}
          onClick={refresh}
        >
          {refreshing ? (
            <Spinner size="sm" label="Checkpoint 목록을 새로고침하고 있습니다." />
          ) : null}
        </IconButton>
      </div>
      {exportedRefreshing ? (
        <LoadingState size="sm">Checkpoint를 불러오고 있습니다.</LoadingState>
      ) : (
        <ExportedCheckpointsSection {...props} share={share} retry={refresh} />
      )}
      <RawCheckpointsSection
        sessionId={controller.session.id}
        state={rawState}
        retry={refresh}
        share={share}
      />
      <CompanionFilesSection
        sessionId={controller.session.id}
        state={companionState}
        retry={refresh}
      />
      {shareIssuer.revealModal}
    </div>
  );
}

// ── 체크포인트 ───────────────────────────────────────────────────────────────

// 어느 체크포인트를 고를지 판단하는 표. 저장 시점 validation_loss 와 그 체크포인트를 평가한 run 의
// 성공률을 함께 표시한다. join 에 실패한 값은 "—" 로 표시한다.
function ExportedCheckpointsSection({
  controller,
  metrics,
  summariesState,
  focusedCheckpoint = null,
  share,
  retry,
}: CheckpointsPanelProps & { share: CheckpointShareAction; retry: () => void }): ReactNode {
  const {
    richState,
    rich,
    downloadCheckpoint,
    downloadingEpoch,
    registerCheckpoint,
    registeringRowId,
  } = controller;

  const summaries = useMemo(
    () => (summariesState.status === "success" ? summariesState.data : []),
    [summariesState],
  );
  const policies = useMemo(() => rich?.policies ?? [], [rich]);
  const validationHistory = metrics.histories[alwaysFetchedMetricKey];
  const validationPoints = useMemo(
    () => (validationHistory?.status === "success" ? validationHistory.data.points : []),
    [validationHistory],
  );
  const rows = useMemo(
    () =>
      createCheckpointTableRows(
        buildCheckpointRows(policies, validationPoints, summaries),
      ).reverse(),
    [policies, validationPoints, summaries],
  );
  const source = useMemo(() => new StaticTableSource(rows), [rows]);
  useEffect(() => () => source.dispose(), [source]);
  const focusedRowId = useMemo(
    () => rows.find((row) => isSameCheckpointTag(row.tag, focusedCheckpoint))?.getRowId() ?? null,
    [rows, focusedCheckpoint],
  );
  const columns = useMemo(
    () =>
      buildCheckpointColumns({
        sessionId: controller.session.id,
        focusedRowId,
        registeringRowId,
        downloadingEpoch,
        registerCheckpoint,
        downloadCheckpoint,
        share,
      }),
    [
      controller.session.id,
      focusedRowId,
      registeringRowId,
      downloadingEpoch,
      registerCheckpoint,
      downloadCheckpoint,
      share,
    ],
  );
  const rowClassName = useCallback(
    (row: CheckpointTableRow) =>
      row.getRowId() === focusedRowId ? styles.checkpointRowFocused : undefined,
    [focusedRowId],
  );

  // 강조 행으로의 스크롤은 focus 요청 하나에 한 번이다. 행은 rich 응답 뒤에 나타나므로 그려진 시점에
  // 맞춘다. 그러나 지표·요약 갱신으로 rows 를 다시 계산해도 사용자가 옮긴 스크롤을 되돌리지 않는다.
  const sectionRef = useRef<HTMLElement | null>(null);
  const scrolledForRef = useRef<CheckpointTag | null>(null);
  useEffect(() => {
    if (focusedRowId === null || scrolledForRef.current === focusedCheckpoint) {
      return;
    }
    scrolledForRef.current = focusedCheckpoint;
    // c6 는 행 ref 를 제공하지 않는다. 그래서 강조 셀의 aria-current 로 그 행을 찾는다.
    // 테스트 DOM 에는 scrollIntoView 가 없을 수 있으므로 있는 환경에서만 호출한다.
    const marker = sectionRef.current?.querySelector('[aria-current="true"]');
    marker?.closest('[role="row"]')?.scrollIntoView?.({ block: "center" });
  }, [focusedRowId, focusedCheckpoint]);

  if (richState.status === "idle" || richState.status === "loading") {
    return <LoadingState size="sm">Checkpoint를 불러오고 있습니다.</LoadingState>;
  }
  const retryAction = { label: "다시 시도", onClick: retry };
  if (richState.status === "error" || rich === null) {
    return (
      <EmptyState size="sm" tone="error" action={retryAction}>
        Checkpoint를 불러오지 못했습니다.
      </EmptyState>
    );
  }
  // 조회 실패와 실제 없음을 구분한다. 상류 policy fan-out 이 잠시 실패했는데 "없음"으로 표시하면,
  // 체크포인트를 실제로 산출한 완료 세션이 산출물 없는 세션으로 읽힌다.
  const checkpointsState = sessionCheckpointsState(rich);
  if (checkpointsState === "unavailable") {
    return (
      <EmptyState size="sm" tone="error" action={retryAction}>
        Checkpoint 목록을 불러오지 못했습니다.
      </EmptyState>
    );
  }
  if (checkpointsState === "empty") {
    return <EmptyState size="sm">이 Session에는 아직 Checkpoint가 없습니다.</EmptyState>;
  }

  return (
    <section className={styles.checkpoints} aria-label="Checkpoint" ref={sectionRef}>
      <HatDataTable
        source={source}
        columns={columns}
        ariaLabel="Checkpoint 목록"
        emptyMessage="이 Session에는 아직 Checkpoint가 없습니다."
        showIndex={false}
        // 창 폭이 좁아도 컬럼 트랙의 최소 폭 합이 표의 하한을 정한다.
        minWidth="0px"
        rowClassName={rowClassName}
      />
      <p className={styles.footnote}>
        다운로드 링크에는 유효 기간이 있습니다. 등록한 Policy는 Evaluation 페이지에서 볼 수
        있습니다.
      </p>
    </section>
  );
}

type CheckpointColumnDeps = {
  sessionId: string;
  // 강조할 행. c6 가 행 속성을 제공하지 않으므로 이름 셀이 aria-current 를 대신 단다.
  focusedRowId: string | null;
  registeringRowId: string | null;
  downloadingEpoch: string | null;
  registerCheckpoint: (checkpoint: SessionCheckpointView) => Promise<void>;
  downloadCheckpoint: (epoch: string) => Promise<void>;
  share: CheckpointShareAction;
};

function buildCheckpointColumns(
  deps: CheckpointColumnDeps,
): readonly DataTableColumn<CheckpointTableRow>[] {
  return [
    {
      key: "label",
      header: "Checkpoint",
      width: "minmax(160px, 1.4fr)",
      render: (row) => (
        <span
          className={styles.checkpointName}
          aria-current={row.getRowId() === deps.focusedRowId ? "true" : undefined}
        >
          {row.label}
          {row.sourceLabel ? <span className={styles.cardMeta}>{row.sourceLabel}</span> : null}
          {row.artifactTail !== null ? (
            // 태그 없는 행은 라벨이 고정 문구라서 artifact 경로 끝부분이 행을 구분한다. 전체 위치는 title 로 표시한다.
            <span className={styles.cardMeta} title={row.checkpoint.artifact_uri ?? undefined}>
              {row.artifactTail}
            </span>
          ) : null}
        </span>
      ),
    },
    { key: "lossLabel", header: "저장 시점 val loss", width: "minmax(110px, 1fr)" },
    {
      key: "evalRuns",
      header: "Evaluation 성공률",
      width: "minmax(140px, 1.2fr)",
      render: (row) => <EvalRateCell evalRuns={row.evalRuns} />,
    },
    {
      key: "sizeLabel",
      header: "크기",
      width: "96px",
      align: "right",
      render: (row) => <span className={styles.cardMeta}>{row.sizeLabel}</span>,
    },
    {
      // 행 액션 seam 대신 컬럼으로 둔다. 그러면 진행 중 라벨("등록 중…")이 바뀔 때 행과 source 를
      // 새로 만들지 않고 컬럼만 다시 만들면 된다. "동작" 헤더도 남는다.
      key: "epoch",
      header: "동작",
      // 버튼 세 개(등록+다운로드+공유)의 natural width 약 236px + 셀 여백. 진행 중 라벨이
      // 잘리지 않을 만큼만 준다.
      width: "264px",
      align: "right",
      render: (row) => (
        <span className={styles.checkpointActions}>
          <IconButton
            text={deps.registeringRowId === row.getRowId() ? "등록 중…" : "Policy로 등록"}
            size="sm"
            variant="surface"
            // 아티팩트 위치가 없는 체크포인트는 정책으로 등록할 수 없다.
            disabled={deps.registeringRowId !== null || row.checkpoint.artifact_uri === null}
            onClick={() => void deps.registerCheckpoint(row.checkpoint)}
          />
          <CheckpointDownloadButton
            epoch={row.epoch}
            downloadingEpoch={deps.downloadingEpoch}
            download={deps.downloadCheckpoint}
          />
          <CheckpointShareButton sessionId={deps.sessionId} row={row} share={deps.share} />
        </span>
      ),
    },
  ];
}

const UNTAGGED_DOWNLOAD_HINT = "태그 없는 산출물은 다운로드 링크가 없습니다.";

// 다운로드는 태그로만 찾는다. 태그 없는 산출물(카탈로그 등록 정책의 artifact 가 태그 디렉터리 밖)은
// 경로가 없으므로 버튼을 비활성으로 두고, 이유는 툴팁이 표시한다. 툴팁 트리거가 wrapper 라서
// disabled 버튼 위에서도 뜬다. 행은 등록 경로를 열어 두려고 남긴다.
function CheckpointDownloadButton({
  epoch,
  downloadingEpoch,
  download,
}: {
  epoch: string | null;
  downloadingEpoch: string | null;
  download: (epoch: string) => Promise<void>;
}): ReactNode {
  if (epoch === null) {
    return (
      <HatTextTooltip text={UNTAGGED_DOWNLOAD_HINT} placement="bottom" align="end">
        <IconButton text="다운로드" size="sm" variant="surface" disabled />
      </HatTextTooltip>
    );
  }
  return (
    <IconButton
      text={downloadingEpoch === epoch ? "준비 중…" : "다운로드"}
      size="sm"
      variant="surface"
      disabled={downloadingEpoch !== null}
      onClick={() => void download(epoch)}
    />
  );
}

const UNTAGGED_SHARE_HINT = "태그 없는 산출물은 공유 링크를 만들 수 없습니다.";

// 공유는 세션 파일 목록에서 그 파일을 찾아 발급한다. 태그 디렉터리 밖 산출물(카탈로그 등록 정책의
// artifact)은 목록에 없다. 그래서 다운로드와 같은 기준으로 비활성으로 두고 이유를 툴팁으로 표시한다.
function CheckpointShareButton({
  sessionId,
  row,
  share,
}: {
  sessionId: string;
  row: CheckpointTableRow;
  share: CheckpointShareAction;
}): ReactNode {
  const artifactUri = row.checkpoint.artifact_uri;
  if (row.epoch === null || artifactUri === null) {
    return (
      <HatTextTooltip text={UNTAGGED_SHARE_HINT} placement="bottom" align="end">
        <IconButton text="공유" size="sm" variant="surface" disabled />
      </HatTextTooltip>
    );
  }
  return (
    <IconButton
      text="공유"
      size="sm"
      variant="surface"
      title="로그인 없이 받을 수 있는 1일짜리 공유 링크를 복사합니다."
      disabled={share.pendingKey !== null}
      onClick={() =>
        share.share(row.getRowId(), {
          kind: "checkpoint",
          session_id: sessionId,
          artifact_uri: artifactUri,
        })
      }
    />
  );
}

// 이 체크포인트를 평가한 run 들의 성공률 요약. run 이 여러 개면 평균과 횟수를 표시하고,
// 시나리오별 상세는 툴팁으로 표시한다.
function EvalRateCell({
  evalRuns,
}: {
  evalRuns: readonly TrainingEvalRunSummaryView[];
}): ReactNode {
  if (evalRuns.length === 0) {
    return <span className={styles.cardMeta}>—</span>;
  }
  const rates = evalRuns
    .map((run) => run.success_rate)
    .filter((rate): rate is number => rate !== null && rate !== undefined);
  if (rates.length === 0) {
    // 빈 이유를 합치지 않는다. 기다리면 값이 올 run 이 하나라도 있으면 "산출 전"이다. 모든 run 이
    // 값이 오지 않는 run(unavailable 계열)이면 "성공률 없음"이다.
    const waiting = evalRuns.some(
      (run) => successRateAbsence(run.success_rate_state) !== "unavailable",
    );
    return (
      <span className={styles.cardMeta}>
        Evaluation {evalRuns.length}회 · {waiting ? "산출 전" : "성공률 없음"}
      </span>
    );
  }
  const mean = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  const label =
    evalRuns.length === 1
      ? `${Math.round(rates[0] * 100)}%`
      : `Evaluation ${evalRuns.length}회 · 평균 ${Math.round(mean * 100)}%`;
  const detail = evalRuns
    .map(
      (run) =>
        `${run.scenario ?? "—"} ${
          run.success_rate != null
            ? `${Math.round(run.success_rate * 100)}%`
            : successRateEmptyLabel(run.success_rate_state)
        }`,
    )
    .join(" · ");
  return (
    <HatTextTooltip text={detail}>
      <span>{label}</span>
    </HatTextTooltip>
  );
}
